import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ViewportScroller } from '@angular/common';
import { ElementRef, Injectable, Injector } from '@angular/core';

import { FeatureGuidePosition } from './feature-guide-position';
import { FeatureGuidePositionService } from './feature-guide-position.service';
import { FeatureGuideTooltip, FeatureGuideTooltipToken } from './feature-guide-tooltip';
import { FeatureGuideTooltipComponent } from './feature-guide-tooltip.component';

@Injectable()
export class FeatureGuideTooltipService {

  private overlayRef: OverlayRef;

  constructor(
    private readonly overlay: Overlay,
    private readonly injector: Injector,
    private readonly featureGuidePositionService: FeatureGuidePositionService,
    private readonly scroller: ViewportScroller
  ) {
    this.overlayRef = overlay.create({
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.reposition()
    });
  }

  show(element: ElementRef, featureGuideTooltip: FeatureGuideTooltip) {
    this.overlayRef.attach(this.featureGuideTooltipComponentPortal(featureGuideTooltip));
    setTimeout(() => {
      const positions = this.getPositions(element.nativeElement, this.overlayRef.overlayElement, featureGuideTooltip.position);
      this.overlayRef.updatePositionStrategy(this.positionStrategyFor(element, positions as ConnectedPosition[]));
      this.scroller.scrollToAnchor('feature-hint');
    });
  }

  hide() {
    this.overlayRef.detach();
  }

  private positionStrategyFor(element: ElementRef, positions: ConnectedPosition[]) {
    return this.overlay.position()
      .flexibleConnectedTo(element.nativeElement)
      .withPositions(positions)
      .withPush(false)
      .withFlexibleDimensions(false);
  }

  private featureGuideTooltipComponentPortal(featureGuideTooltip: FeatureGuideTooltip) {
    return new ComponentPortal(FeatureGuideTooltipComponent, null, Injector.create({
      parent: this.injector,
      providers: [
        {provide: FeatureGuideTooltipToken, useValue: featureGuideTooltip}
      ]
    }));
  }

  private getPositions(hostElement: HTMLElement, tooltipElement: HTMLElement, position: FeatureGuidePosition): ConnectedPosition[] {
    const positionData = {
      position,
      hostSize: {width: hostElement.offsetWidth, height: hostElement.offsetHeight},
      tooltipSize: {width: tooltipElement.offsetWidth, height: tooltipElement.offsetHeight}
    };
    return this.featureGuidePositionService.calculatePosition(positionData);
  }
}
